import { OutgoingInvoiceTypeEnum } from "@src/__generated__/urql-graphql";
import { sumBy } from "lodash";
import { computed, makeObservable, observable } from "mobx";
import type { InvoiceData, OutgoingInvoiceData, ProformaInvoiceData } from ".";
import { Contact } from "./Contact";
import { Project } from "./Project";

const isOutgoingInvoice = (
  invoice: InvoiceData,
): invoice is OutgoingInvoiceData => {
  return (invoice as OutgoingInvoiceData).orders !== undefined;
};

const isProformaInvoice = (
  invoice: InvoiceData,
): invoice is ProformaInvoiceData => {
  return (invoice as OutgoingInvoiceData).orders === undefined;
};

export class Invoice {
  id: InvoiceData["id"];
  type?: OutgoingInvoiceData["type"];
  invoice_no: InvoiceData["invoice_no"];
  currency: InvoiceData["currency"];
  issue_date: InvoiceData["issue_date"];
  date_of_supply?: OutgoingInvoiceData["date_of_supply"];
  due_date: InvoiceData["due_date"];
  paid_at: InvoiceData["paid_at"];
  note: InvoiceData["note"];
  internal_note: InvoiceData["internal_note"];
  text_on_invoice: InvoiceData["text_on_invoice"];
  brands: InvoiceData["brands"];
  billingEntity: InvoiceData["billingEntity"];
  invoiceItems: InvoiceData["invoiceItems"];
  orders?: OutgoingInvoiceData["orders"];
  proformaInvoices: OutgoingInvoiceData["proformaInvoicesPivot"] = [];
  relatesToOutgoingProforma: OutgoingInvoiceData["relatesToOutgoingProformaInvoice"];
  stats: InvoiceData["stats"];
  ourWorkBudgetSplits?: OutgoingInvoiceData["ourWorkBudgetSplits"];
  expenseBudgetSplits?: OutgoingInvoiceData["expenseBudgetSplits"];
  prepaymentSplits?: OutgoingInvoiceData["prepaymentSplits"];
  filesToAttach: InvoiceData["filesToAttach"];
  invoicePdf: InvoiceData["invoicePdf"];
  proofOfPayments: ProformaInvoiceData["proofOfPayments"];
  teams: InvoiceData["teams"];
  submittedAt: InvoiceData["request_for_invoicing_submit_date"];
  createdAt: InvoiceData["request_for_invoicing_created_date"];
  projectTimeBilling?: OutgoingInvoiceData["projectTimeBilling"];
  invoicePaymentMethod?: InvoiceData["invoicePaymentMethod"];
  exchange_rate: InvoiceData["exchange_rate"];
  @observable
  payments: NonNullable<InvoiceData["accountMoves"]>;
  @observable.ref modelLock: InvoiceData["modelLock"];

  contact: Contact | null;
  projects: Project[];

  constructor(invoice: InvoiceData) {
    makeObservable(this);
    this.id = invoice.id;
    this.invoice_no = invoice.invoice_no;
    this.currency = invoice.currency;
    this.issue_date = invoice.issue_date;
    this.paid_at = invoice.paid_at;
    this.due_date = invoice.due_date;
    this.note = invoice.note;
    this.internal_note = invoice.internal_note;
    this.text_on_invoice = invoice.text_on_invoice;
    this.brands = invoice.brands;
    this.billingEntity = invoice.billingEntity;
    this.invoiceItems = invoice.invoiceItems;
    this.stats = invoice.stats;
    this.filesToAttach = invoice.filesToAttach;
    this.invoicePdf = invoice.invoicePdf;
    this.contact = invoice.contact ? new Contact(invoice.contact) : null;
    this.submittedAt = invoice.request_for_invoicing_submit_date;
    this.createdAt = invoice.request_for_invoicing_created_date;
    this.modelLock = invoice.modelLock;
    this.invoicePaymentMethod = invoice.invoicePaymentMethod ?? undefined;
    this.exchange_rate = invoice.exchange_rate;
    this.payments = invoice.accountMoves ?? [];
    if (isOutgoingInvoice(invoice)) {
      this.type = invoice.type;
      this.date_of_supply = invoice.date_of_supply;
      this.proformaInvoices = invoice.proformaInvoicesPivot;
      this.relatesToOutgoingProforma = invoice.relatesToOutgoingProformaInvoice;
      this.orders = invoice.orders;
      this.ourWorkBudgetSplits = invoice.ourWorkBudgetSplits;
      this.expenseBudgetSplits = invoice.expenseBudgetSplits;
      this.prepaymentSplits = invoice.prepaymentSplits;
      this.projectTimeBilling = invoice.projectTimeBilling;
      this.proofOfPayments = invoice.invoiceProofOfPayments.map(
        (invoiceProof) => invoiceProof.invoice,
      );
    }
    if (isProformaInvoice(invoice)) {
      this.proofOfPayments = invoice.proofOfPayments;
    }
    this.projects = invoice.projects.map(
      (project) =>
        new Project(
          project,
          this.ourWorkBudgetSplits,
          this.expenseBudgetSplits,
        ),
    );
    this.teams = invoice.teams;
  }

  get isInvoice(): boolean {
    return !!this.type && this.type === OutgoingInvoiceTypeEnum.Invoice;
  }

  get isProformaPayment(): boolean {
    return !!this.type && this.type === OutgoingInvoiceTypeEnum.ProformaPayment;
  }

  get isReconcilliationInvoice(): boolean {
    if (!this.proofOfPayments) return false;
    return this.proofOfPayments.length > 0;
  }

  @computed get totalPaid(): number {
    return sumBy(this.payments, (payment) => payment.amount);
  }

  @computed get hasModelLock(): boolean {
    return !!this.modelLock && this.modelLock.is_locked;
  }
}
